import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { Stage, Layer, Line, Rect, Circle, Text, Group, Arrow } from 'react-konva';
import Konva from 'konva';
import { useTranslation } from '@structura/i18n';
import { createWoodPattern, createConcretePattern, createSteelPattern } from './MaterialPatterns';

// Import types from SMBeamDesigner
interface SMBeamNode {
  id: number;
  supportType: 'Fixed' | 'Pinned' | 'Roller' | 'Free';
  axialFixed: boolean;
  position: number;
}

interface SMBeamLoad {
  id: string;
  type: 'UniformLoad' | 'PointLoad' | 'TriangularRight' | 'TriangularLeft' | 'AxialForce';
  spanId: number;
  category?: 'Permanent' | 'Variable' | 'Wind' | 'Snow' | 'Prestress';
  pointLoad: number;
  pointLoadPosition: number;
  normalForce: number;
  normalForcePosition: number;
  normalForceEccentricity: number;
  uniformIntensity: number;
  startIntensity: number;
  endIntensity: number;
  startPosition: number;
  endPosition: number;
  axialForceEnd?: 'left' | 'right';
}

interface SMBeamSpan {
  id: number;
  leftNodeId: number;
  rightNodeId: number;
  length: number;
  E: number;
  A: number;
  I: number;
  loads: SMBeamLoad[];
}

interface CrossSection {
  name: string;
  width: number;
  height: number;
  area: number;
  inertia: number;
  isCustom: boolean;
  key?: string;
  material?: string;
  sectionType?: 'I-beam' | 'H-beam' | 'rectangular' | 'circular' | 'T-beam' | 'L-beam';
  webThickness?: number;
  flangeThickness?: number;
  sectionClass?: 'auto' | '1' | '2' | '3' | '4';
}

interface SMPTCable {
  id: string;
  prestressForce: number;
  nodeEccentricities: number[];
  midSpanEccentricities: number[];
}

interface SMBeamModel {
  nodes: SMBeamNode[];
  spans: SMBeamSpan[];
  loadCases: any[];
  crossSection: CrossSection;
  ptCables?: SMPTCable[];
}

interface BeamKonvaVisualizationProps {
  model: SMBeamModel;
  material?: string;
  width?: number;
  height?: number;
  storageKey?: string;
  provideCapture?: (capture: () => string) => void;
}

const BeamKonvaVisualization: React.FC<BeamKonvaVisualizationProps> = ({ 
  model, 
  material = 'steel',
  width: initialWidth = 800, 
  height: initialHeight = 300,
  storageKey = 'beamVisualization_size',
  provideCapture
}) => {
  const { t } = useTranslation();
  const stageRef = useRef<Konva.Stage | null>(null);

  useEffect(() => {
    if (!provideCapture) return;
    const capture = () => {
      try {
        return stageRef.current?.toDataURL({ pixelRatio: 2, mimeType: 'image/png' }) || '';
      } catch {
        return '';
      }
    };
    provideCapture(capture);
  }, [provideCapture]);

  interface StageSize { width: number; height: number }
  const [stageSize, setStageSize] = useState<StageSize>(() => {
    try {
      const saved = localStorage.getItem(storageKey)
      if (saved) {
        const parsed = JSON.parse(saved) as Partial<StageSize>
        return {
          width: Math.max(400, parsed.width || initialWidth),
          height: Math.max(200, parsed.height || initialHeight)
        }
      }
    } catch { /* localStorage unavailable */ }
    return { width: initialWidth, height: initialHeight }
  });
  const [zoom, setZoom] = useState(1);
  const [stagePos, setStagePos] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);

  // Calculate total beam length
  const totalLength = useMemo(() =>
    model.spans.reduce((sum, span) => sum + span.length, 0),
    [model.spans]
  );

  // Calculate scale for beam drawing
  const beamScale = useMemo(() =>
    (stageSize.width - 100) / totalLength,
    [stageSize.width, totalLength]
  );

  const beamY = stageSize.height / 2;
  const beamHeight = 20;
  const startX = 50;

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(stageSize))
    } catch { /* localStorage unavailable */ }
  }, [stageSize, storageKey])

  // Get beam colors based on material
  const { beamFill, beamStroke } = useMemo(() => {
    let fill = '#e9ecef';
    let stroke = '#6c757d';

    if (material === 'steel') {
      fill = '#f8f9fa';
      stroke = '#495057';
    } else if (material === 'timber') {
      fill = '#c19a6b'; // Camel/wood brown
      stroke = '#8b4513'; // Saddle brown
    } else if (material === 'concrete') {
      fill = '#d6d8db';
      stroke = '#6c757d';
    }

    return { beamFill: fill, beamStroke: stroke };
  }, [material]);

  // Create material pattern images for beam
  const [beamPatternImage, setBeamPatternImage] = useState<HTMLImageElement | null>(null);

  useEffect(() => {
    let patternDataUrl: string;

    // Create pattern based on material
    if (material === 'timber') {
      patternDataUrl = createWoodPattern({ width: 100, height: 100, scale: 1 });
    } else if (material === 'concrete') {
      patternDataUrl = createConcretePattern({ width: 100, height: 100, scale: 1 });
    } else if (material === 'steel') {
      patternDataUrl = createSteelPattern({ width: 100, height: 100, scale: 1 }, 'solid');
    } else {
      // Default pattern
      patternDataUrl = createSteelPattern({ width: 100, height: 100, scale: 1 }, 'solid');
    }

    // Load pattern image
    const img = new Image();
    img.onload = () => {
      setBeamPatternImage(img);
    };
    img.src = patternDataUrl;
  }, [material]);

  // Calculate max loads for proportional scaling
  const { maxVerticalLoad, maxAxialLoad } = useMemo(() => {
    const allLoads = model.spans.flatMap(span => span.loads);
    
    const verticalLoadValues = allLoads.flatMap(load => {
      if (load.type === 'TriangularRight' || load.type === 'TriangularLeft') {
        return [Math.abs(load.startIntensity), Math.abs(load.endIntensity)];
      } else if (load.type === 'UniformLoad') {
        return [Math.abs(load.uniformIntensity)];
      } else if (load.type === 'PointLoad') {
        return [Math.abs(load.pointLoad)];
      }
      return [];
    });
    
    const axialLoadValues = allLoads.flatMap(load => {
      if (load.type === 'AxialForce') {
        return [Math.abs(load.normalForce)];
      }
      return [];
    });
    
    return {
      maxVerticalLoad: Math.max(...verticalLoadValues, 1),
      maxAxialLoad: Math.max(...axialLoadValues, 1)
    };
  }, [model.spans]);

  // Render support symbol
  const renderSupport = (node: SMBeamNode, x: number) => {
    const supportColors = {
      Fixed: '#dc3545',
      Pinned: '#28a745',
      Roller: '#ffc107',
      Free: '#6c757d'
    };

    const color = supportColors[node.supportType];

    switch (node.supportType) {
      case 'Fixed':
        return (
          <Group key={`support-${node.id}`}>
            <Rect
              x={x - 8}
              y={beamY + beamHeight / 2}
              width={16}
              height={25}
              fill={color}
            />
            {/* Hatching lines */}
            {[0, 1, 2, 3].map(i => (
              <Line
                key={`hatch-${i}`}
                points={[x - 8 + i * 4, beamY + beamHeight / 2 + 25, x - 8 + i * 4 + 3, beamY + beamHeight / 2 + 20]}
                stroke="white"
                strokeWidth={1}
              />
            ))}
            <Text
              x={x}
              y={beamY + beamHeight / 2 + 30}
              text={node.id.toString()}
              fontSize={12}
              fill="black"
              offsetX={6}
              align="center"
            />
          </Group>
        );

      case 'Pinned':
        return (
          <Group key={`support-${node.id}`}>
            <Line
              points={[x, beamY + beamHeight / 2, x - 12, beamY + beamHeight / 2 + 25, x + 12, beamY + beamHeight / 2 + 25]}
              closed
              fill={color}
            />
            <Line
              points={[x - 12, beamY + beamHeight / 2 + 25, x + 12, beamY + beamHeight / 2 + 25]}
              stroke="black"
              strokeWidth={2}
            />
            <Text
              x={x}
              y={beamY + beamHeight / 2 + 30}
              text={node.id.toString()}
              fontSize={12}
              fill="black"
              offsetX={6}
              align="center"
            />
          </Group>
        );

      case 'Roller':
        return (
          <Group key={`support-${node.id}`}>
            <Line
              points={[x, beamY + beamHeight / 2, x - 12, beamY + beamHeight / 2 + 20, x + 12, beamY + beamHeight / 2 + 20]}
              closed
              fill={color}
            />
            {/* Roller circles */}
            <Circle
              x={x - 6}
              y={beamY + beamHeight / 2 + 23}
              radius={3}
              fill="white"
              stroke={color}
              strokeWidth={1}
            />
            <Circle
              x={x + 6}
              y={beamY + beamHeight / 2 + 23}
              radius={3}
              fill="white"
              stroke={color}
              strokeWidth={1}
            />
            <Text
              x={x}
              y={beamY + beamHeight / 2 + 30}
              text={node.id.toString()}
              fontSize={12}
              fill="black"
              offsetX={6}
              align="center"
            />
          </Group>
        );

      default:
        return null;
    }
  };

  // Render uniform load
  const renderUniformLoad = (load: SMBeamLoad, spanX: number, spanWidth: number) => {
    const loadValue = load.uniformIntensity;
    const loadScale = Math.abs(loadValue) / maxVerticalLoad;
    const arrowLength = 20 + (loadScale * 40);
    const arrowSpacing = Math.max(15, spanWidth / 10);
    const color = loadValue >= 0 ? '#28a745' : '#dc3545';
    
    const arrows: React.ReactElement[] = [];
    const numArrows = Math.ceil(spanWidth / arrowSpacing) + 1;
    
    for (let i = 0; i < numArrows; i++) {
      const arrowX = spanX + Math.min(i * arrowSpacing, spanWidth);
      const arrowHeadSize = 3 + loadScale * 2;
      
      arrows.push(
        <Arrow
          key={`uniform-arrow-${i}`}
          points={[
            arrowX, beamY,
            arrowX, loadValue >= 0 ? beamY - arrowLength : beamY + arrowLength
          ]}
          pointerLength={arrowHeadSize}
          pointerWidth={arrowHeadSize}
          fill={color}
          stroke={color}
          strokeWidth={2}
        />
      );
    }

    const labelY = loadValue >= 0 ? beamY - arrowLength - 10 : beamY + arrowLength + 20;
    
    return (
      <Group key={`uniform-load-${load.id}`}>
        {arrows}
        <Text
          x={spanX + spanWidth / 2}
          y={labelY}
          text={`${loadValue.toFixed(4)} kN/m`}
          fontSize={12}
          fill={color}
          offsetX={40}
          align="center"
        />
      </Group>
    );
  };

  // Render point load
  const renderPointLoad = (load: SMBeamLoad, spanX: number, span: SMBeamSpan) => {
    const loadValue = load.pointLoad;
    const loadScale = Math.abs(loadValue) / maxVerticalLoad;
    const arrowLength = 20 + (loadScale * 40);
    const loadX = spanX + (load.pointLoadPosition / span.length) * (span.length * beamScale);
    const color = loadValue >= 0 ? '#28a745' : '#dc3545';
    const arrowHeadSize = 4 + loadScale * 3;

    const labelY = loadValue >= 0 ? beamY - arrowLength - 10 : beamY + arrowLength + 20;

    return (
      <Group key={`point-load-${load.id}`}>
        <Arrow
          points={[
            loadX, beamY,
            loadX, loadValue >= 0 ? beamY - arrowLength : beamY + arrowLength
          ]}
          pointerLength={arrowHeadSize}
          pointerWidth={arrowHeadSize}
          fill={color}
          stroke={color}
          strokeWidth={2 + loadScale * 2}
        />
        <Text
          x={loadX}
          y={labelY}
          text={`${loadValue.toFixed(4)} kN`}
          fontSize={10 + loadScale * 4}
          fill={color}
          offsetX={30}
          align="center"
        />
      </Group>
    );
  };

  // Render triangular load
  const renderTriangularLoad = (
    load: SMBeamLoad, 
    spanX: number, 
    span: SMBeamSpan,
    isRightTriangular: boolean
  ) => {
    const startX = spanX + (load.startPosition / span.length) * (span.length * beamScale);
    const endX = spanX + (load.endPosition / span.length) * (span.length * beamScale);
    const loadWidth = endX - startX;
    const minVal = isRightTriangular ? load.startIntensity : load.endIntensity;
    const maxVal = isRightTriangular ? load.endIntensity : load.startIntensity;

    const samples = 10;
    const arrows: React.ReactElement[] = [];

    // Find the maximum absolute intensity for scaling arrow lengths
    const maxAbsIntensity = Math.max(Math.abs(minVal), Math.abs(maxVal));

    for (let i = 0; i <= samples; i++) {
      const ratio = i / samples;
      const posX = startX + ratio * loadWidth;
      const v = isRightTriangular
        ? minVal + ratio * (maxVal - minVal)
        : maxVal + ratio * (minVal - maxVal);

      // Skip drawing arrows at zero-intensity points
      if (Math.abs(v) < 1e-6) continue;

      const localScale = Math.abs(v) / maxVerticalLoad;
      // Scale arrow length proportionally — zero intensity = zero length
      const intensityRatio = maxAbsIntensity > 0 ? Math.abs(v) / maxAbsIntensity : 0;
      const len = intensityRatio * (20 + localScale * 40);
      const color = v > 0 ? '#28a745' : '#dc3545';
      const headSize = 3 + localScale * 2;

      arrows.push(
        <Arrow
          key={`tri-arrow-${i}`}
          points={[
            posX, beamY,
            posX, v > 0 ? beamY - len : beamY + len
          ]}
          pointerLength={headSize}
          pointerWidth={headSize}
          fill={color}
          stroke={color}
          strokeWidth={2}
        />
      );
    }

    const dominant = Math.abs(maxVal) >= Math.abs(minVal) ? maxVal : minVal;
    const labelLen = 20 + (Math.abs(dominant) / maxVerticalLoad) * 40;
    const labelY = dominant >= 0 ? beamY - labelLen - 10 : beamY + labelLen + 20;
    const color = dominant >= 0 ? '#28a745' : '#dc3545';

    return (
      <Group key={`tri-load-${load.id}`}>
        {arrows}
        <Text
          x={startX + loadWidth / 2}
          y={labelY}
          text={`${isRightTriangular ? minVal.toFixed(4) : maxVal.toFixed(4)}→${isRightTriangular ? maxVal.toFixed(4) : minVal.toFixed(4)} kN/m`}
          fontSize={11}
          fill={color}
          offsetX={50}
          align="center"
        />
      </Group>
    );
  };

  // Render axial force with eccentricity visualization
  // When eccentricity = 0, arrow is on beam centerline
  // When eccentricity != 0, arrow is offset from centerline and shows the moment effect
  const renderAxialForce = (load: SMBeamLoad, spanX: number, spanWidth: number) => {
    const axialValue = load.normalForce;
    if (Math.abs(axialValue) < 0.001) return null;

    const eccentricity = load.normalForceEccentricity || 0;
    const axialScale = Math.abs(axialValue) / maxAxialLoad;
    const arrowLength = 30 + (axialScale * 40);
    const isLeftEnd = load.normalForcePosition === 0 || (load.axialForceEnd === 'left');
    const arrowX = isLeftEnd ? spanX : spanX + spanWidth;
    
    // Scale eccentricity to screen coordinates (1m = 100px approximately)
    // Positive eccentricity = above centerline, negative = below
    const eccentricityScale = 100; // pixels per meter
    const eccentricityOffset = eccentricity * eccentricityScale;
    
    // Base position is beam centerline when e=0, offset by eccentricity otherwise
    const baseArrowY = beamY;
    const arrowY = baseArrowY - eccentricityOffset; // negative because screen Y increases downward
    
    const color = axialValue >= 0 ? '#ffc107' : '#6f42c1';
    const headSize = 5 + axialScale * 3;

    let startX: number;
    let endX: number;

    if (isLeftEnd) {
      if (axialValue >= 0) {
        startX = arrowX;
        endX = arrowX - arrowLength;
      } else {
        startX = arrowX - arrowLength;
        endX = arrowX;
      }
    } else {
      if (axialValue >= 0) {
        startX = arrowX;
        endX = arrowX + arrowLength;
      } else {
        startX = arrowX + arrowLength;
        endX = arrowX;
      }
    }

    // Build label text
    let labelText = `${axialValue.toFixed(2)} kN ${axialValue >= 0 ? '(T)' : '(C)'}`;
    if (Math.abs(eccentricity) > 0.001) {
      const moment = axialValue * eccentricity;
      labelText += `\ne=${(eccentricity * 1000).toFixed(0)}mm → M=${moment.toFixed(2)} kNm`;
    }

    return (
      <Group key={`axial-load-${load.id}`}>
        {/* Show eccentricity offset line from centerline to load application point */}
        {Math.abs(eccentricity) > 0.001 && (
          <>
            {/* Dashed line showing eccentricity offset */}
            <Line
              points={[arrowX, baseArrowY, arrowX, arrowY]}
              stroke={color}
              strokeWidth={1}
              dash={[4, 4]}
            />
            {/* Small circle at beam centerline showing reference point */}
            <Circle
              x={arrowX}
              y={baseArrowY}
              radius={3}
              fill="white"
              stroke={color}
              strokeWidth={1}
            />
          </>
        )}
        
        {/* Axial force arrow at eccentric position */}
        <Arrow
          points={[startX, arrowY, endX, arrowY]}
          pointerLength={headSize}
          pointerWidth={headSize}
          fill={color}
          stroke={color}
          strokeWidth={2 + axialScale * 2}
        />
        
        {/* Label with force value and eccentricity info */}
        <Text
          x={isLeftEnd ? arrowX - arrowLength / 2 : arrowX + arrowLength / 2}
          y={arrowY - (Math.abs(eccentricity) > 0.001 ? 30 : 15)}
          text={labelText}
          fontSize={10 + axialScale * 2}
          fill={color}
          offsetX={40}
          align="center"
        />
      </Group>
    );
  };

  // Render PT Cable profile as a smooth curve
  const renderPTCables = () => {
    const ptCables = model.ptCables;
    if (!ptCables || ptCables.length === 0) return null;

    // Scale for eccentricity visualization (m to pixels)
    // Typical eccentricities are 0.1-0.3m, scale to visible range
    const eccScale = beamHeight * 3; // Scale factor for eccentricity display

    return ptCables.map((cable, cableIndex) => {
      const points: number[] = [];
      const nodeEcc = cable.nodeEccentricities;
      const midEcc = cable.midSpanEccentricities;
      
      // Build curve points through nodes and mid-spans
      for (let spanIndex = 0; spanIndex < model.spans.length; spanIndex++) {
        const span = model.spans[spanIndex];
        const spanStartX = startX + model.spans.slice(0, spanIndex).reduce((sum, s) => sum + s.length * beamScale, 0);
        const spanWidth = span.length * beamScale;
        
        // Get eccentricities for this span
        const leftEcc = nodeEcc[spanIndex] ?? 0;
        const rightEcc = nodeEcc[spanIndex + 1] ?? 0;
        const midSpanEcc = midEcc[spanIndex] ?? 0;
        
        // Generate points along the parabolic profile
        // Parabola: e(t) = e_left + (e_right - e_left)*t + 4*(e_mid - e_avg)*t*(1-t)
        // where t = x/L, e_avg = (e_left + e_right)/2
        const numPointsPerSpan = 21;
        for (let i = 0; i <= numPointsPerSpan; i++) {
          // Skip first point of subsequent spans to avoid duplicates
          if (spanIndex > 0 && i === 0) continue;
          
          const t = i / numPointsPerSpan;
          const x = spanStartX + t * spanWidth;
          
          const eAvg = (leftEcc + rightEcc) / 2;
          const sag = midSpanEcc - eAvg;
          const eLinear = leftEcc + t * (rightEcc - leftEcc);
          const eParabolic = 4 * sag * t * (1 - t);
          const ecc = eLinear + eParabolic;
          
          // Y position: beam center + eccentricity * scale
          // Positive eccentricity = below neutral axis = +Y (down in screen coords)
          const y = beamY + ecc * eccScale;
          
          points.push(x, y);
        }
      }
      
      // Draw the cable profile
      const cableColor = 'darkcyan';
      
      return (
        <Group key={`ptcable-${cable.id}-${cableIndex}`}>
          {/* Cable line */}
          <Line
            points={points}
            stroke={cableColor}
            strokeWidth={3}
            tension={0}
            lineCap="round"
            lineJoin="round"
          />
          
          {/* Draw anchor points at supports */}
          {model.nodes.map((node, nodeIndex) => {
            const nodeX = startX + model.spans.slice(0, nodeIndex).reduce((sum, s) => sum + s.length * beamScale, 0);
            const ecc = nodeEcc[nodeIndex] ?? 0;
            const nodeY = beamY + ecc * eccScale;
            
            return (
              <Circle
                key={`ptcable-anchor-${cable.id}-${nodeIndex}`}
                x={nodeX}
                y={nodeY}
                radius={5}
                fill={cableColor}
                stroke="white"
                strokeWidth={1}
              />
            );
          })}
          
          {/* Label with prestress force */}
          <Text
            x={startX + totalLength / 2}
            y={beamY + beamHeight / 2 + 75}
            text={`PT Cable: P = ${cable.prestressForce.toFixed(0)} kN`}
            fontSize={11}
            fill={cableColor}
            offsetX={50}
            align="center"
            fontStyle="bold"
          />
        </Group>
      );
    });
  };

  // Handle zoom
  const handleWheel = useCallback((e: Konva.KonvaEventObject<WheelEvent>) => {
    e.evt.preventDefault();
    const stage = e.target.getStage();
    if (!stage) return;

    const oldScale = stage.scaleX();
    const pointer = stage.getPointerPosition();
    if (!pointer) return;

    const mousePointTo = {
      x: (pointer.x - stage.x()) / oldScale,
      y: (pointer.y - stage.y()) / oldScale,
    };

    const newScale = e.evt.deltaY > 0 ? oldScale * 0.9 : oldScale * 1.1;
    const clampedScale = Math.max(0.1, Math.min(5, newScale));

    setZoom(clampedScale);

    const newPos = {
      x: pointer.x - mousePointTo.x * clampedScale,
      y: pointer.y - mousePointTo.y * clampedScale,
    };

    setStagePos(newPos);
  }, []);

  // Reset view
  const resetView = useCallback(() => {
    setZoom(1);
    setStagePos({ x: 0, y: 0 });
  }, []);

  // Fit all
  const fitAll = useCallback(() => {
    const margin = 100;
    const availableWidth = stageSize.width - margin;
    const scaleX = availableWidth / totalLength;
    const optimalZoom = Math.min(scaleX, 2);
    
    setZoom(optimalZoom);
    setStagePos({ x: margin / 2, y: 0 });
  }, [stageSize.width, totalLength]);

  // Resize handler
  const handleResize = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const startY = e.clientY;
    const startWidth = stageSize.width;
    const startHeight = stageSize.height;

    const handleMouseMove = (e: MouseEvent) => {
      const newWidth = Math.max(400, startWidth + (e.clientX - startX));
      const newHeight = Math.max(200, startHeight + (e.clientY - startY));
      setStageSize({ width: newWidth, height: newHeight });
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [stageSize]);

  return (
    <div style={{ position: 'relative', width: 'fit-content', border: '1px solid #dee2e6', borderRadius: '0.375rem', overflow: 'hidden' }}>
      {/* Control buttons */}
      <div style={{
        position: 'absolute',
        top: '8px',
        right: '8px',
        zIndex: 10,
        display: 'flex',
        gap: '4px'
      }}>
        <button
          onClick={fitAll}
          className="btn btn-sm btn-outline-primary"
          title={t('beam.viz.fitAll')}
          style={{ fontSize: '12px', padding: '2px 6px' }}
        >
          📐
        </button>
        <button
          onClick={resetView}
          className="btn btn-sm btn-outline-secondary"
          title={t('beam.viz.resetView')}
          style={{ fontSize: '12px', padding: '2px 6px' }}
        >
          🔄
        </button>
        <span className="badge bg-secondary" style={{ fontSize: '10px', padding: '2px 4px' }}>
          {Math.round(zoom * 100)}%
        </span>
      </div>

      <Stage
        width={stageSize.width}
        height={stageSize.height}
        scaleX={zoom}
        scaleY={zoom}
        x={stagePos.x}
        y={stagePos.y}
        onWheel={handleWheel}
        draggable
        onDragStart={() => setIsDragging(true)}
        onDragEnd={(e) => {
          setIsDragging(false);
          setStagePos({ x: e.target.x(), y: e.target.y() });
        }}
        style={{ 
          cursor: isDragging ? 'grabbing' : 'grab',
          backgroundColor: 'white',
          border: '1px solid #dee2e6',
          borderRadius: '4px'
        }}
        ref={(node) => { stageRef.current = node as unknown as Konva.Stage; }}
      >
        <Layer>
          {/* Draw beam spans */}
          {model.spans.map((span, index) => {
            const spanX = startX + model.spans.slice(0, index).reduce((sum, s) => sum + s.length * beamScale, 0);
            const spanWidth = span.length * beamScale;

            return (
              <Group key={`span-${span.id}`}>
                <Rect
                  x={spanX}
                  y={beamY - beamHeight / 2}
                  width={spanWidth}
                  height={beamHeight}
                  fillPatternImage={beamPatternImage || undefined}
                  fillPatternRepeat="repeat"
                  fill={beamPatternImage ? undefined : beamFill}
                  stroke={beamStroke}
                  strokeWidth={2}
                />

                {/* Draw loads */}
                {span.loads.map(load => {
                  if (load.type === 'UniformLoad') {
                    return renderUniformLoad(load, spanX, spanWidth);
                  } else if (load.type === 'PointLoad') {
                    return renderPointLoad(load, spanX, span);
                  } else if (load.type === 'TriangularRight') {
                    return renderTriangularLoad(load, spanX, span, true);
                  } else if (load.type === 'TriangularLeft') {
                    return renderTriangularLoad(load, spanX, span, false);
                  } else if (load.type === 'AxialForce') {
                    return renderAxialForce(load, spanX, spanWidth);
                  }
                  return null;
                })}

                {/* Dimension line */}
                <Line
                  points={[spanX, beamY + beamHeight / 2 + 50, spanX + spanWidth, beamY + beamHeight / 2 + 50]}
                  stroke="#666"
                  strokeWidth={1}
                />
                <Text
                  x={spanX + spanWidth / 2}
                  y={beamY + beamHeight / 2 + 55}
                  text={`${span.length}m`}
                  fontSize={11}
                  fill="#666"
                  offsetX={15}
                  align="center"
                />
              </Group>
            );
          })}

          {/* Draw supports */}
          {model.nodes.map((node, index) => {
            const nodeX = startX + model.spans.slice(0, index).reduce((sum, s) => sum + s.length * beamScale, 0);
            return renderSupport(node, nodeX);
          })}

          {/* Draw PT cables */}
          {renderPTCables()}
        </Layer>
      </Stage>

      {/* Resize handle */}
      <div
        style={{
          position: 'absolute',
          bottom: '0',
          right: '0',
          width: '16px',
          height: '16px',
          background: 'linear-gradient(-45deg, transparent 30%, #6c757d 30%, #6c757d 70%, transparent 70%)',
          cursor: 'se-resize',
          opacity: 0.6,
          zIndex: 10
        }}
        onMouseDown={handleResize}
        title={t('beam.viz.dragToResize')}
      />

      {/* Instructions */}
      <div style={{
        position: 'absolute',
        bottom: '4px',
        left: '8px',
        fontSize: '11px',
        color: '#6c757d',
        pointerEvents: 'none'
      }}>
        Scroll: Zoom • Drag: Pan • Corner: Resize
      </div>
    </div>
  );
};

export default BeamKonvaVisualization;

